import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { type RegisterPayload, registerFormSchema, useRegister } from 'src/features/auth/api';
import { commonMessages, registerMessages } from 'src/features/auth/utils';
import { useGetInvitationEmail } from 'src/features/users/api/useGetInvitationEmail';
import { InputWithPassword } from 'src/registry/components/inputs/InputWithPassword';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { CUSTOM_ERROR_PREFIX, SingleAttributeError } from 'src/utils/errors';
import { validationMessages } from 'src/utils/messages';

const RegisterForm = () => {
    const { invitationParkingId, invitationId } = useParams();
    const navigate = useNavigate();

    const {
        data: invitation,
        isLoading: isLoadingInvitation,
        isError: isInvitationError,
    } = useGetInvitationEmail({
        config: { enabled: !!invitationId && !!invitationParkingId },
        parkingId: invitationParkingId!,
        invitationId: invitationId!,
    });

    const { formatMessage } = useIntl();

    function useFormatCustomError() {
        return (error: SingleAttributeError) => {
            const id = `${CUSTOM_ERROR_PREFIX}${error.code}`;
            return formatMessage({ id, defaultMessage: error.detail });
        };
    }

    const format = useFormatCustomError();

    const { mutate: register, isPending } = useRegister({
        onSuccess: () => {
            navigate('/', { replace: true });
        },
        onError: (e) => {
            if (e?.response?.data) {
                const errorData = e.response.data;
                errorData.errors.forEach((error) => {
                    const errorMessage = format(error as SingleAttributeError);

                    if (error.code) {
                        form.setError('password', {
                            type: 'manual',
                            message: errorMessage,
                        });
                        return;
                    }
                });
            }
        },
    });

    const form = useForm<RegisterPayload>({
        resolver: zodResolver(registerFormSchema),
        mode: 'onChange',
        values: {
            first_name: '',
            last_name: '',
            password: '',
            email: invitation?.email ?? '',
            confirm_password: '',
        },
    });

    const onSubmit = ({ confirm_password: _, ...formValues }: RegisterPayload) => {
        const registerValues = {
            ...formValues,
            invitation_id: invitationId,
            parking_id: invitationParkingId,
        };
        register(registerValues);
    };

    return (
        <>
            <div className="flex w-full max-w-sm flex-col items-center py-6 text-center">
                <h1 className="text-2xl font-semibold">{formatMessage(registerMessages.welcomeAboard)}</h1>
                <h3 className="text-center text-gray-500">{formatMessage(registerMessages.registerMessage)}</h3>
            </div>
            {isLoadingInvitation && (
                <Form form={form} onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-sm">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                </Form>
            )}
            {isInvitationError && (
                <p className="h-4 text-sm text-red-500">
                    <FormattedMessage {...registerMessages.invalidInvitation} />
                </p>
            )}
            {!isLoadingInvitation && !isInvitationError && (
                <Form form={form} onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-sm">
                    <FormField
                        control={form.control}
                        name="first_name"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{formatMessage(commonMessages.firstName)}</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder={formatMessage(commonMessages.firstNamePlaceholder)}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage>{formatMessage(validationMessages.validationErrorFirstName)}</FormMessage>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="last_name"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{formatMessage(commonMessages.lastName)}</FormLabel>
                                <FormControl>
                                    <Input placeholder={formatMessage(commonMessages.lastNamePlaceholder)} {...field} />
                                </FormControl>
                                <FormMessage>{formatMessage(validationMessages.validationErrorLastName)}</FormMessage>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{formatMessage(commonMessages.email)}</FormLabel>
                                <FormControl>
                                    <Input
                                        type="email"
                                        disabled
                                        placeholder={formatMessage(commonMessages.emailPlaceholder)}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage>{formatMessage(registerMessages.registerEmailTip)}</FormMessage>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{formatMessage(commonMessages.password)}</FormLabel>
                                <FormControl>
                                    <InputWithPassword type="password" placeholder="••••••••" {...field} />
                                </FormControl>
                                <FormMessage>{formatMessage(validationMessages.validationErrorPassword)}</FormMessage>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="confirm_password"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{formatMessage(commonMessages.confirmPassword)}</FormLabel>
                                <FormControl>
                                    <InputWithPassword type="password" placeholder="••••••••" {...field} />
                                </FormControl>
                                <FormMessage>{formatMessage(registerMessages.registerConfirmPasswordTip)}</FormMessage>
                            </FormItem>
                        )}
                    />
                    <Button
                        className="w-full"
                        type="submit"
                        disabled={!form.formState.isDirty || !form.formState.isValid || isPending}
                    >
                        {formatMessage(registerMessages.register)}
                    </Button>
                </Form>
            )}
        </>
    );
};

export { RegisterForm };
